import { BoardFragment, Widget, PlotFragment } from '../../generated/graphql';

export interface BoardState {
  board: BoardFragment;
  selectedChart: PlotFragment | null;
  openChartModal: (selectedChart: PlotFragment) => void;
  closeChartModal: () => void;
}

export enum BoardActionTypes {
  SetBoard,
  AddWidgets,
  SetWidgets,
  RemoveWidget,
  SetTitle,
  SetDescription,
  SetPinned,
  SetFilterNode,
  CloseChartModal,
  OpenChartModal,
}

type SetBoardPayload = { board: BoardFragment };
type AddWidgetsPayload = { widgets: Widget[] };
type SetWidgetsPayload = { widgets: Widget[] };
type RemoveWidgetPayload = { widgetId: number };
type SetTitlePayload = { title: string };
type SetDescriptionPayload = { description: string };
type SetPinnedPayload = { isPinned: boolean };
type SetFilterNodePayload = { filterNode: string };
type OpenChartModalPayload = { selectedChart: PlotFragment };

export type BoardAction =
  | { type: BoardActionTypes.SetBoard; payload: SetBoardPayload }
  | { type: BoardActionTypes.AddWidgets; payload: AddWidgetsPayload }
  | { type: BoardActionTypes.SetWidgets; payload: SetWidgetsPayload }
  | { type: BoardActionTypes.RemoveWidget; payload: RemoveWidgetPayload }
  | { type: BoardActionTypes.SetTitle; payload: SetTitlePayload }
  | { type: BoardActionTypes.SetDescription; payload: SetDescriptionPayload }
  | { type: BoardActionTypes.SetPinned; payload: SetPinnedPayload }
  | { type: BoardActionTypes.SetFilterNode; payload: SetFilterNodePayload }
  | { type: BoardActionTypes.CloseChartModal }
  | { type: BoardActionTypes.OpenChartModal; payload: OpenChartModalPayload };
export const BoardReducer = (state: BoardState, action: BoardAction): BoardState => {
  if (action.type === BoardActionTypes.SetBoard) {
    return { ...state, board: action.payload.board };
  } else {
    if (!state.board) throw new Error('Cannot execute action without a board');
    switch (action.type) {
      case BoardActionTypes.SetWidgets:
        return {
          ...state,
          board: {
            ...state.board,
            widgets: action.payload.widgets,
          },
        };
      case BoardActionTypes.AddWidgets:
        return {
          ...state,
          board: {
            ...state.board,
            widgets: [...state.board.widgets, ...action.payload.widgets],
          },
        };
      case BoardActionTypes.SetTitle:
        return {
          ...state,
          board: {
            ...state.board,
            title: action.payload.title,
          },
        };
      case BoardActionTypes.SetDescription:
        return {
          ...state,
          board: {
            ...state.board,
            description: action.payload.description,
          },
        };
      case BoardActionTypes.SetPinned:
        return {
          ...state,
          board: {
            ...state.board,
            isPinnedByUser: action.payload.isPinned,
          },
        };
      case BoardActionTypes.RemoveWidget:
        return {
          ...state,
          board: {
            ...state.board,
            widgets: state.board.widgets.filter((widget) => widget.id !== action.payload.widgetId),
          },
        };
      /**
       * This is the filter that is saved to the board. Note that this is a stringified json of the filter node.
       *
       * DO NOT BIND STATE TO THIS. This is not the place to consume the filters that are applied to the board. Use the filter context for that
       * The filter context handles changing filters, binding state, and all the dynamism that's associated with the filters.
       */
      case BoardActionTypes.SetFilterNode:
        return {
          ...state,
          board: {
            ...state.board,
            filterNode: action.payload.filterNode,
          },
        };
      case BoardActionTypes.OpenChartModal:
        return {
          ...state,
          selectedChart: action.payload.selectedChart,
        };
      case BoardActionTypes.CloseChartModal:
        return {
          ...state,
          selectedChart: null,
        };
      default:
        throw new Error(`Unhandled action type`);
    }
  }
};
